import React from "react";
import { formatUnits } from "viem";
import { GitBranch, Zap, TrendingDown } from "lucide-react";

/**
 * Route compare — reads /advanced/routes from the swap widget.
 * Falls back to a single-route view when only one quote is available.
 */
export default function RouteCompare({ ctx, routes = [], onSelect, selectedIndex = 0 }) {
  const list = (routes && routes.length ? routes : ctx?.raw ? [ctx.raw] : []);
  const toToken = ctx?.toToken;

  if (list.length === 0) {
    return (
      <div className="px-4 py-8 text-center">
        <div className="mx-auto w-10 h-10 rounded-lg bg-[var(--panel-2)] border border-[var(--border)] flex items-center justify-center mb-3">
          <GitBranch size={16} className="text-[var(--text-muted)]" />
        </div>
        <div className="text-[13px] text-[var(--text)] font-medium">Route comparison</div>
        <div className="text-[12px] text-[var(--text-muted)] mt-1 max-w-[240px] mx-auto">
          Enter an amount to see up to 3 competing routes across 45+ DEXs and bridges.
        </div>
      </div>
    );
  }

  const val = (r) => BigInt(r?.estimate?.toAmount || r?.toAmount || 0);
  const byPrice = [...list].sort((a, b) => (val(b) > val(a) ? 1 : -1));
  const byTime = [...list].sort((a, b) =>
    (a?.estimate?.executionDuration || 999) - (b?.estimate?.executionDuration || 999));
  const bestPriceId = byPrice[0]?.id || byPrice[0]?.tool;
  const fastestId = byTime[0]?.id || byTime[0]?.tool;

  return (
    <div className="px-3 sm:px-4 py-4 space-y-2">
      <div className="text-[10.5px] text-[var(--text-subtle)] uppercase tracking-wider mb-1">
        {list.length} route{list.length > 1 ? "s" : ""} available
      </div>
      {list.slice(0, 5).map((r, i) => {
        const est = r.estimate || r;
        const out = est.toAmount && toToken
          ? Number(formatUnits(BigInt(est.toAmount), toToken.decimals)) : null;
        const key = r.id || r.tool || i;
        const tool = r.tool || r.toolDetails?.name || (r.steps?.[0]?.tool) || "Direct";
        const time = est.executionDuration;
        const isBest = key === bestPriceId;
        const isFast = key === fastestId;
        const selected = i === selectedIndex;
        return (
          <button key={key} onClick={() => onSelect && onSelect(i)}
            className={`w-full text-left panel-3 p-3 transition-colors ${selected ? "!border-[var(--accent)]/50 bg-[var(--accent-soft)]" : "hover:bg-[var(--panel-2)]"}`}
            data-testid={`route-option-${i}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <span className="w-7 h-7 rounded-md bg-[var(--panel-2)] border border-[var(--border)] text-[11px] font-bold font-mono flex items-center justify-center text-[var(--text)] flex-shrink-0">{i + 1}</span>
                <div className="min-w-0">
                  <div className="text-[13px] text-[var(--text)] font-medium truncate">via {tool}</div>
                  <div className="text-[10.5px] text-[var(--text-muted)] font-mono">
                    {(r.steps?.length || 1)} step{(r.steps?.length || 1) > 1 ? "s" : ""}
                    {time ? ` · ${time}s` : ""}
                  </div>
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="num-md text-[var(--text)]">
                  {out != null ? (out < 1 ? out.toFixed(6) : out.toLocaleString(undefined, { maximumFractionDigits: 4 })) : "—"}
                </div>
                <div className="text-[10.5px] text-[var(--text-muted)] font-mono">{toToken?.symbol}</div>
              </div>
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {isBest && <span className="pill pill-up"><TrendingDown size={10} /> Best price</span>}
              {isFast && <span className="pill pill-accent"><Zap size={10} /> Fastest</span>}
              {selected && <span className="pill pill-accent">Selected</span>}
            </div>
          </button>
        );
      })}
    </div>
  );
}
